import React, { useState, useEffect } from 'react';
import { AlertTriangle, TrendingUp, Users, Briefcase, UserCheck, X } from 'lucide-react';
import { fetchCompanyUsageStats, type UsageStats } from '../../services/companyCustomizationApi';
import { useCompany } from '../../contexts/CompanyContext';
import { useI18n } from '../../contexts/I18nContext';

interface LimitsWidgetProps {
  onClose?: () => void;
  compact?: boolean;
}

const LimitsWidget: React.FC<LimitsWidgetProps> = ({ onClose, compact = false }) => {
  const { t } = useI18n();
  const { currentCompany } = useCompany();
  const [stats, setStats] = useState<UsageStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (currentCompany) {
      loadStats();
    }
  }, [currentCompany]);

  const loadStats = async () => {
    if (!currentCompany) return;
    
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCompanyUsageStats(currentCompany.id);
      setStats(data);
    } catch (err: any) {
      console.error('[LimitsWidget] Failed to load stats', err);
      setError('Não foi possível carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  if (!currentCompany) {
    return null;
  }

  // Filtrar apenas recursos que precisam de atenção (>= 80% ou excedidos)
  const attentionNeeded = stats.filter(s => s.percentage >= 80 || s.is_exceeded);

  if (loading) {
    return (
      <div className="bg-white p-4 rounded-lg shadow-md">
        <div className="text-gray-500">{t('limits.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white p-4 rounded-lg shadow-md border border-red-200">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  if (attentionNeeded.length === 0 && !compact) {
    // Se não há alertas e não é modo compacto, não mostrar widget
    return null;
  }

  const getResourceIcon = (resourceType: string) => {
    switch (resourceType) {
      case 'users':
        return Users;
      case 'employees':
        return Briefcase;
      case 'clients':
        return UserCheck;
      default:
        return TrendingUp;
    }
  };

  const getResourceLabel = (resourceType: string) => {
    switch (resourceType) {
      case 'users':
        return 'Usuários';
      case 'employees':
        return 'Funcionários';
      case 'clients':
        return 'Clientes';
      default:
        return resourceType;
    }
  };

  const getAlertColor = (stat: UsageStats) => {
    if (stat.is_exceeded) return 'bg-red-50 border-red-300 text-red-800';
    if (stat.percentage >= 90) return 'bg-orange-50 border-orange-300 text-orange-800';
    return 'bg-yellow-50 border-yellow-300 text-yellow-800';
  };

  const getProgressColor = (stat: UsageStats) => {
    if (stat.is_exceeded) return 'bg-red-500';
    if (stat.percentage >= 90) return 'bg-orange-500';
    return 'bg-yellow-500';
  };

  if (compact) {
    // Modo compacto: mostrar apenas se houver alertas
    if (attentionNeeded.length === 0) {
      return null;
    }

    return (
      <div className="bg-white p-3 rounded-lg shadow-md border-l-4 border-yellow-500">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-yellow-600" />
            <span className="text-sm font-semibold text-gray-800">{t('common.resourceLimits')}</span>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
          )}
        </div>
        <div className="space-y-2">
          {attentionNeeded.slice(0, 2).map((stat) => {
            const Icon = getResourceIcon(stat.resource_type);
            return (
              <div key={stat.resource_type} className="text-xs">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1">
                    <Icon size={14} />
                    <span>{getResourceLabel(stat.resource_type)}</span>
                  </div>
                  <span className="font-semibold">
                    {stat.current} / {stat.limit}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full ${getProgressColor(stat)}`}
                    style={{ width: `${Math.min(stat.percentage, 100)}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
        {attentionNeeded.length > 2 && (
          <div className="text-xs text-gray-500 mt-2">
            +{attentionNeeded.length - 2} mais
          </div>
        )}
      </div>
    );
  }

  // Modo completo: mostrar todos os recursos
  return (
    <div className="bg-white p-4 rounded-lg shadow-md">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp size={20} className="text-indigo-600" />
          <h3 className="text-lg font-semibold text-gray-800">{t('common.resourceLimits')}</h3>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {attentionNeeded.length > 0 && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={18} className="text-red-600" />
            <span className="text-sm font-semibold text-red-800">
              Atenção: {attentionNeeded.length} recurso(s) precisam de atenção
            </span>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {stats.map((stat) => {
          const Icon = getResourceIcon(stat.resource_type);
          const needsAttention = stat.percentage >= 80 || stat.is_exceeded;
          
          return (
            <div
              key={stat.resource_type}
              className={`p-3 rounded-lg border ${
                needsAttention ? getAlertColor(stat) : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Icon size={18} />
                  <span className="font-medium">{getResourceLabel(stat.resource_type)}</span>
                  {stat.is_exceeded && (
                    <span className="text-xs bg-red-600 text-white px-2 py-0.5 rounded">
                      Excedido
                    </span>
                  )}
                </div>
                <span className="text-sm font-semibold">
                  {stat.current} / {stat.limit}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
                <div
                  className={`h-2 rounded-full ${getProgressColor(stat)}`}
                  style={{ width: `${Math.min(stat.percentage, 100)}%` }}
                ></div>
              </div>
              <div className="text-xs text-gray-600">
                {stat.percentage.toFixed(1)}% utilizado
              </div>
            </div>
          );
        })}
      </div>

      {stats.length === 0 && (
        <div className="text-center text-gray-500 py-4">
          Nenhum limite configurado
        </div>
      )}
    </div>
  );
};

export default LimitsWidget;

